/* ==========================================================================
   NEW CART — drawer controller (self-contained, no Dawn global.js dependency)
   Part 1: open / close · scroll-lock · qty change · remove (minus at min)
           · re-render via Section Rendering API
   Custom elements:  <new-cart-drawer>  ·  <new-cart-items>
   Public API:  window.NewCart.open() / .close() / .refresh()
   Events (listened): 'new-cart:open', 'new-cart:refresh', 'cart:refresh'
   Events (fired):    'new-cart:opened', 'new-cart:closed', 'new-cart:updated'
   ========================================================================== */
(function () {
  'use strict';

  var CART_ICON_SELECTOR = '#cart-icon-bubble';

  /* ---------- scroll lock — block wheel/touch/keys, no CSS side effects ----------
     position:fixed on <body> (the old technique here) knocks any position:sticky
     descendant — the product page's gallery rail — out of its stuck offset and
     snaps it to a different spot the instant the drawer opens (confirmed live:
     opening this drawer from a product page jumped the sticky rail ~1234px).
     Blocking the scroll-causing events instead never touches body/html, so
     sticky keeps working and the page never visibly moves. */
  var NC_SCROLLABLE = '.nc-drawer__scroll';
  var NC_SCROLL_KEYS = { ' ': 1, 'PageUp': 1, 'PageDown': 1, 'Home': 1, 'End': 1, 'ArrowUp': 1, 'ArrowDown': 1, 'ArrowLeft': 1, 'ArrowRight': 1 };
  function ncBlockWheel(e) {
    if (e.target && e.target.closest && e.target.closest(NC_SCROLLABLE)) return;
    e.preventDefault();
  }
  function ncBlockKeys(e) {
    if (!NC_SCROLL_KEYS[e.key]) return;
    var a = document.activeElement;
    if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)) return;
    if (e.target && e.target.closest && e.target.closest(NC_SCROLLABLE)) return;
    e.preventDefault();
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function parseHTML(str) {
    return new DOMParser().parseFromString(str, 'text/html');
  }

  function applyBubble(sections) {
    if (!sections || !sections['cart-icon-bubble']) return;
    // cart-icon-bubble.liquid renders the icon and the item-count as SIBLINGS
    // (<span id="cart-icon-bubble"> icon </span><span class="wbhcartitem"> count </span>).
    // Querying #cart-icon-bubble in the fetched fragment only grabs the icon
    // span, dropping the count — pull from .shopify-section instead (Shopify's
    // automatic wrapper around every AJAX-rendered section), which contains
    // both. Matches the same extraction Dawn's own cart.js uses for this.
    var fresh = parseHTML(sections['cart-icon-bubble']).querySelector('.shopify-section');
    var cur = document.querySelector('#cart-icon-bubble');
    if (fresh && cur) cur.innerHTML = fresh.innerHTML;
  }

  /* ==================================================================
     <new-cart-drawer>
     ================================================================== */
  class NewCartDrawer extends HTMLElement {
    connectedCallback() {
      if (this._wired) return;
      this._wired = true;

      this.sectionId = this.dataset.sectionId || 'new-cart-drawer';

      // delegated — survives innerHTML swaps of the panel (close, recs nav, quick-add)
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-nc-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        var shipToggle = e.target.closest('[data-nc-ship-toggle]');
        if (shipToggle) {
          e.preventDefault();
          var ship = shipToggle.closest('[data-nc-ship]');
          if (ship) this.toggleShip(ship);
          return;
        }

        var nav = e.target.closest('[data-nc-recs-next]');
        if (nav) {
          e.preventDefault();
          this.scrollRecs();
          return;
        }

        var sizesOpen = e.target.closest('[data-nc-sizes-open]');
        if (sizesOpen) {
          e.preventDefault();
          var openMedia = sizesOpen.closest('.nc-rec__media');
          this.querySelectorAll('.nc-rec__media.is-picking').forEach(function (m) {
            if (m !== openMedia) m.classList.remove('is-picking');
          });
          if (openMedia) openMedia.classList.add('is-picking');
          return;
        }

        var sizesClose = e.target.closest('[data-nc-sizes-close]');
        if (sizesClose) {
          e.preventDefault();
          var closeMedia = sizesClose.closest('.nc-rec__media');
          if (closeMedia) closeMedia.classList.remove('is-picking');
          return;
        }

        var add = e.target.closest('[data-nc-quickadd]');
        if (add) {
          e.preventDefault();
          this.quickAdd(add);
        }
      });

      this._onKey = (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      };

      this.bindCartIcon();
      this.initRecsDrag();

      document.addEventListener('new-cart:open', () => this.open());
      document.addEventListener('new-cart:refresh', () => this.refresh());
      document.addEventListener('cart:refresh', () => this.refresh());

      window.NewCart = {
        open: () => this.open(),
        close: () => this.close(),
        refresh: () => this.refresh(),
      };
    }

    /* recommendations: click-and-drag to scroll (mouse/pen; touch uses native) */
    railInfo(track) {
      var rail = track && track.querySelector('[data-nc-recs-rail]');
      if (!rail) return null;
      var m = /translate3d\(\s*(-?[\d.]+)/.exec(rail.style.transform);
      var card = rail.querySelector('.nc-rec');
      return {
        rail: rail,
        x: m ? parseFloat(m[1]) : 0,
        max: Math.max(0, rail.scrollWidth - track.clientWidth),
        step: card ? card.getBoundingClientRect().width + 1 : Math.round(track.clientWidth * 0.5),
      };
    }

    /* clamp x to [-max, 0] and snap to the nearest card edge */
    snapRailX(x, info) {
      var s = Math.round(x / info.step) * info.step;
      if (s > 0) s = 0;
      if (s < -info.max) s = -info.max;
      return s;
    }

    setRailX(rail, x) {
      rail.style.transform = 'translate3d(' + Math.round(x * 100) / 100 + 'px, 0, 0)';
    }

    /* recommendations: transform-based drag with rAF batching + momentum glide */
    initRecsDrag() {
      var self = this;
      var drag = null;
      var frame = 0;

      var render = function () {
        frame = 0;
        if (!drag) return;
        var raw = drag.baseX + (drag.curX - drag.startX);
        var x = raw;
        if (raw > 0) x = raw * 0.35;
        else if (raw < -drag.max) x = -drag.max + (raw + drag.max) * 0.35;
        self.setRailX(drag.rail, x);
      };

      this.addEventListener('pointerdown', function (e) {
        if (e.button != null && e.button !== 0) return;
        // pressing down directly on the quick-add button must never start a
        // drag (setPointerCapture below retargets the eventual pointerup to
        // the track, which on desktop was swallowing the button's click —
        // the add-to-cart never fired even though nothing looked wrong).
        if (e.target.closest('[data-nc-quickadd], [data-nc-sizes-open], [data-nc-sizes-close]')) return;
        var track = e.target.closest('[data-nc-recs-track]');
        var info = self.railInfo(track);
        if (!info) return;
        drag = {
          track: track, rail: info.rail, max: info.max,
          startX: e.clientX, curX: e.clientX, baseX: info.x,
          lastX: e.clientX, lastT: e.timeStamp, vel: 0, moved: false,
          pointerId: e.pointerId,
        };
      });

      var onMove = function (e) {
        if (!drag) return;
        drag.curX = e.clientX;
        var dt = e.timeStamp - drag.lastT;
        if (dt > 0) drag.vel = (e.clientX - drag.lastX) / dt;
        drag.lastX = e.clientX;
        drag.lastT = e.timeStamp;
        if (!drag.moved && Math.abs(drag.curX - drag.startX) > 3) {
          drag.moved = true;
          drag.track.classList.add('is-dragging');
          if (drag.pointerId != null && drag.track.setPointerCapture) {
            try { drag.track.setPointerCapture(drag.pointerId); } catch (err) {}
          }
        }
        if (!drag.moved) return;
        if (!frame) frame = requestAnimationFrame(render);
      };

      var onUp = function () {
        if (!drag) return;
        var d = drag;
        drag = null;
        if (frame) { cancelAnimationFrame(frame); frame = 0; }
        d.track.classList.remove('is-dragging');

        var info = self.railInfo(d.track);
        if (info) {
          var projected = info.x + d.vel * 220; // momentum projection
          self.setRailX(info.rail, self.snapRailX(projected, info)); // land on a card edge
        }

        if (d.moved) {
          var kill = function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
          };
          d.track.addEventListener('click', kill, true);
          setTimeout(function () {
            d.track.removeEventListener('click', kill, true);
          }, 60);
        }
      };

      document.addEventListener('pointermove', onMove, { passive: true });
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onUp);
    }

    bindCartIcon() {
      if (this.classList.contains('nc-drawer--inline')) return; // static /cart page, nothing to open
      var icon = document.querySelector(CART_ICON_SELECTOR);
      if (!icon || icon._ncBound) return;
      icon._ncBound = true;
      icon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    }

    lockScroll() {
      window.addEventListener('wheel', ncBlockWheel, { passive: false });
      window.addEventListener('touchmove', ncBlockWheel, { passive: false });
      window.addEventListener('keydown', ncBlockKeys, true);
    }

    unlockScroll() {
      window.removeEventListener('wheel', ncBlockWheel, { passive: false });
      window.removeEventListener('touchmove', ncBlockWheel, { passive: false });
      window.removeEventListener('keydown', ncBlockKeys, true);
    }

    open() {
      if (this.classList.contains('is-open')) return;
      this.lockScroll();
      this.classList.add('is-open');
      document.addEventListener('keydown', this._onKey);
      this.dispatchEvent(new CustomEvent('new-cart:opened', { bubbles: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onKey);
      this.dispatchEvent(new CustomEvent('new-cart:closed', { bubbles: true }));
      this.unlockScroll();
    }

    /* full section re-fetch — used after external cart changes */
    refresh() {
      return fetch(window.location.pathname + '?section_id=' + this.sectionId)
        .then((r) => r.text())
        .then((text) => this.replacePanel(parseHTML(text)))
        .catch(() => {});
    }

    /* apply a { sectionId: html } map returned by /cart/change.js */
    applySections(sections) {
      if (!sections) return;
      if (sections[this.sectionId]) {
        this.replacePanel(parseHTML(sections[this.sectionId]));
      }
      applyBubble(sections);
    }

    replacePanel(doc) {
      var fresh = doc.querySelector('.nc-drawer__panel');
      var current = this.querySelector('.nc-drawer__panel');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }

    /* Shipping & Returns: smooth open/close by animating real height */
    toggleShip(ship) {
      var wrap = ship.querySelector('.nc-ship__body-wrap');
      if (!wrap) return;

      if (wrap._ncEnd) {
        wrap.removeEventListener('transitionend', wrap._ncEnd);
        wrap._ncEnd = null;
      }

      if (ship.classList.contains('is-open')) {
        // close: lock current height, reflow, then collapse to 0
        wrap.style.height = wrap.scrollHeight + 'px';
        void wrap.offsetHeight;
        ship.classList.remove('is-open');
        wrap.style.height = '0px';
      } else {
        // open: expand to content height, then release to auto
        ship.classList.add('is-open');
        wrap.style.height = wrap.scrollHeight + 'px';
        wrap._ncEnd = function (e) {
          if (e.propertyName !== 'height') return;
          wrap.style.height = 'auto';
          wrap.removeEventListener('transitionend', wrap._ncEnd);
          wrap._ncEnd = null;
        };
        wrap.addEventListener('transitionend', wrap._ncEnd);
      }
    }

    /* recommendations: advance one card per click, wrap once past the end */
    scrollRecs() {
      var track = this.querySelector('[data-nc-recs-track]');
      var info = this.railInfo(track);
      if (!info) return;
      if (info.x <= -info.max + 2) {
        this.setRailX(info.rail, 0); // at the end → back to start
        return;
      }
      var idx = Math.round(-info.x / info.step); // current card
      var next = -(idx + 1) * info.step; // advance exactly one, snapped
      if (next < -info.max) next = -info.max;
      this.setRailX(info.rail, next);
    }

    /* recommendations: single-variant quick-add */
    quickAdd(btn) {
      var id = btn.getAttribute('data-variant-id');
      if (!id || btn.classList.contains('is-busy')) return;
      btn.classList.add('is-busy');

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: id,
          quantity: 1,
          sections: [this.sectionId, 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          if (state.status) {
            btn.classList.remove('is-busy');
            return;
          }
          this.applySections(state.sections);
          document.dispatchEvent(
            new CustomEvent('new-cart:updated', { bubbles: true, detail: state })
          );
        })
        .catch(() => btn.classList.remove('is-busy'));
    }
  }
  customElements.define('new-cart-drawer', NewCartDrawer);

  /* ==================================================================
     <new-cart-items>  — wraps the <form>; owns qty / remove
     ================================================================== */
  class NewCartItems extends HTMLElement {
    connectedCallback() {
      this.drawer = this.closest('new-cart-drawer');
      this.sectionId =
        this.dataset.sectionId || (this.drawer && this.drawer.sectionId) || 'new-cart-drawer';

      // JS owns qty/remove; only let the real checkout submit through
      var form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          var sub = e.submitter || document.activeElement;
          if (sub && sub.name === 'checkout') return;
          e.preventDefault();
        });
      }

      this.addEventListener('click', (e) => {
        var minus = e.target.closest('[data-nc-minus]');
        var plus = e.target.closest('[data-nc-plus]');
        var remove = e.target.closest('[data-nc-remove]');
        if (!minus && !plus && !remove) return;
        e.preventDefault();

        var row = e.target.closest('[data-nc-item]');
        if (!row || row.classList.contains('is-busy')) return;

        if (remove) {
          this.change(row, 0);
          return;
        }

        var input = row.querySelector('[data-nc-qty]');
        var current = parseInt(input.value, 10) || 0;
        var min = parseInt(input.getAttribute('min'), 10) || 1;
        var step = parseInt(input.getAttribute('step'), 10) || 1;
        var next;

        if (plus) {
          next = current + step;
        } else {
          next = current - step;
          if (next < min) next = 0; // minus at the floor removes the line
        }
        this.change(row, next);
      });

      this.addEventListener('change', (e) => {
        var input = e.target.closest('[data-nc-qty]');
        if (!input) return;
        var row = input.closest('[data-nc-item]');
        var val = parseInt(input.value, 10);
        if (isNaN(val) || val < 0) val = parseInt(input.defaultValue, 10) || 1;
        this.change(row, val);
      });
    }

    change(row, quantity) {
      var key = row.getAttribute('data-nc-key');
      row.classList.add('is-busy');
      this.clearError(row);

      fetch(root() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: [this.sectionId, 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })
        .then((r) => r.json())
        .then((state) => {
          if (state.status) {
            // Shopify error payload (e.g. stock cap reached)
            this.showError(row, state.description || state.message);
            row.classList.remove('is-busy');
            return;
          }
          if (this.drawer) this.drawer.applySections(state.sections);
          else this.applyPageSections(state.sections);
          row.classList.remove('is-busy'); // no-op if the row was swapped out
          document.dispatchEvent(
            new CustomEvent('new-cart:updated', { bubbles: true, detail: state })
          );
        })
        .catch(() => {
          this.showError(row, '');
          row.classList.remove('is-busy');
        });
    }

    /* /cart page: re-render the section body in place (no drawer present) */
    applyPageSections(sections) {
      if (!sections) return;
      if (sections[this.sectionId]) {
        var fresh = parseHTML(sections[this.sectionId]).querySelector('.nc-page__inner');
        var host = document.querySelector('#new-cart-page .nc-page__inner');
        if (fresh && host) host.innerHTML = fresh.innerHTML;
      }
      applyBubble(sections);
    }

    showError(row, msg) {
      var box = row.querySelector('[data-nc-error]');
      if (!box) return;
      box.textContent = msg || box.getAttribute('data-nc-error') || '';
      box.classList.add('is-shown');
    }

    clearError(row) {
      var box = row.querySelector('[data-nc-error]');
      if (box) box.classList.remove('is-shown');
    }
  }
  customElements.define('new-cart-items', NewCartItems);
})();
